<template>
  <mobile-filters
    v-if="activeTab"
    :filters="mobileFilterConfig"
    @update:filter="handleFilterUpdate"
    @apply="$emit('apply')"
    @reset="$emit('reset')"
  >
    <template v-slot:actions>
      <slot name="actions"></slot>
    </template>

    <!-- Custom filter slots for warehouse-specific filters -->
    <template v-slot:filter-warehouse>
      <autocomplete
        :label="$t('stock')"
        :items="warehouses"
        :value="selectedWarehouses"
        @input="$emit('update:selectedWarehouses', $event)"
        return-object
        clearable
        chips
        selectable-all
        small-chips
        deletable-chips
        hide-details
        multiple
        item-text="name"
        item-value="id"
        outlined
      >
        <template v-slot:prepend-item>
          <v-list-item @click="$emit('toggle-warehouses')">
            <v-list-item-icon>
              <v-icon>mdi-select-all</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t("display_all") }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </autocomplete>
    </template>

    <template v-slot:filter-supplier>
      <autocomplete
        :label="$t('supplier')"
        :items="suppliers"
        :value="selectedSuppliers"
        @input="$emit('update:selectedSuppliers', $event)"
        return-object
        clearable
        chips
        selectable-all
        small-chips
        deletable-chips
        hide-details
        multiple
        item-text="name"
        item-value="id"
        outlined
      >
        <template v-slot:prepend-item>
          <v-list-item @click="$emit('toggle-suppliers')">
            <v-list-item-icon>
              <v-icon>mdi-select-all</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t("display_all") }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </autocomplete>
    </template>

    <template v-slot:filter-groceryCode>
      <grocery-picker
        :value="filters.groceryCode"
        @input="handleFilterUpdate({ key: 'groceryCode', value: $event })"
        :groceries="codeFilterGroceries"
        hide-details
        outlined
      />
    </template>
  </mobile-filters>
</template>

<script>
import MobileFilters from "@/components/global/MobileFilters.vue";
import GroceryPicker from "@/components/groceries/fields/GroceryPicker.vue";
import warehouseFiltersMixin from "@/mixins/warehouse/warehouseFiltersMixin";

export default {
  name: "WarehouseFiltersMobile",
  components: {
    MobileFilters,
    GroceryPicker,
  },
  mixins: [warehouseFiltersMixin],
  props: {
    activeTab: {
      type: Object,
      default: null,
    },
    warehouses: {
      type: Array,
      default: () => [],
    },
    selectedWarehouses: {
      type: Array,
      default: () => [],
    },
    selectedSuppliers: {
      type: Array,
      default: () => [],
    },
    suppliers: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      required: true,
    },
    codeFilterGroceries: {
      type: Array,
      default: () => [],
    },
    shifts: {
      type: Array,
      default: () => [1, 2, 3, 4],
    },
  },
  computed: {
    mobileFilterConfig() {
      const config = {};

      // Warehouse filter
      if (this.activeTab?.filters?.warehouse) {
        config.warehouse = {
          type: "custom",
          label: this.$t("stock"),
          icon: "mdi-warehouse",
          value: this.selectedWarehouses,
          chipText: this.warehouseChipText,
          visible: this.selectedWarehouses.length > 0,
          clearable: false,
        };
      }

      // Show type filter
      if (this.activeTab?.filters?.show_type) {
        config.showType = {
          type: "dropdown",
          label: this.$t("view"),
          icon: "mdi-eye",
          items: this.showItems,
          value: this.filters.showType,
          chipText:
            this.showItems.find((i) => i.value === this.filters.showType)?.text ||
            this.filters.showType,
          visible: !!this.filters.showType,
          clearable: false,
        };
      }

      // Shifts filter
      if (this.activeTab?.filters?.shifts) {
        config.selectedShifts = {
          type: "autocomplete",
          label: this.$t("shifts"),
          icon: "mdi-clock-outline",
          items: this.shifts,
          value: this.filters.selectedShifts,
          multiple: true,
          chipText:
            this.filters.selectedShifts?.length > 0
              ? this.filters.selectedShifts.join(", ")
              : this.$t("shifts"),
          visible: this.filters.selectedShifts?.length > 0,
          clearable: true,
        };
      }

      // Date filter
      if (this.activeTab?.filters?.date_from) {
        const isSingleDate = !this.activeTab?.filters?.date_to;
        const isReadonly = this.activeTab?.filters?.date_from_readonly;
        config.dates = {
          type: "date",
          label: this.$t("date"),
          icon: "mdi-calendar",
          value: isSingleDate
            ? this.filters.dates.from
            : [this.filters.dates.from, this.filters.dates.to],
          singleDate: isSingleDate,
          readonly: isReadonly,
          chipText: this.formatDateRange || this.$t("date"),
          visible: true,
          clearable: !isReadonly,
        };
      }

      // Document type filter (show_order)
      if (this.activeTab?.filters?.show_order) {
        config.documentType = {
          type: "dropdown",
          label: this.$t("warehouse.document_type"),
          icon: "mdi-file-document",
          items: this.documentTypeItems,
          itemText: "name",
          itemValue: "value",
          value: this.filters.documentType,
          chipText:
            this.documentTypeItems.find((i) => i.value === this.filters.documentType)
              ?.name || this.$t("warehouse.document_type"),
          visible: !!this.filters.documentType,
          clearable: true,
        };
      }

      // Status filter
      if (this.activeTab?.filters?.state_filter) {
        config.status = {
          type: "dropdown",
          label: this.$t("status"),
          icon: "mdi-flag",
          items: this.statusTypes,
          itemText: "name",
          itemValue: "value",
          value: this.filters.status,
          chipText:
            this.statusTypes.find((i) => i.value === this.filters.status)?.name ||
            this.$t("status"),
          visible: !!this.filters.status,
          clearable: true,
        };
      }

      // Transaction type filter
      if (this.activeTab?.filters?.type_filter) {
        config.transactionType = {
          type: "dropdown",
          label: this.$t("warehouse.document_type"),
          icon: "mdi-swap-horizontal",
          items: this.transactionTypes,
          value: this.filters.documentType,
          chipText:
            this.transactionTypes.find((i) => i.value === this.filters.documentType)
              ?.text || this.$t("warehouse.document_type"),
          visible: !!this.filters.documentType,
          clearable: true,
        };
      }

      // Grocery code filter
      if (this.activeTab?.filters?.code_filter) {
        config.groceryCode = {
          type: "custom",
          label: this.$t("grocery_code"),
          icon: "mdi-barcode",
          value: this.filters.groceryCode,
          chipText: this.filters.groceryCode || this.$t("grocery_code"),
          visible: !!this.filters.groceryCode,
          clearable: true,
        };
      }

      // Supplier filter
      if (this.activeTab?.filters?.suppliers && this.suppliers.length > 0) {
        config.supplier = {
          type: "custom",
          label: this.$t("supplier"),
          icon: "mdi-truck",
          value: this.selectedSuppliers,
          chipText:
            this.selectedSuppliers.length > 0
              ? `${this.selectedSuppliers.length} ${this.$t("supplier")}`
              : this.$t("supplier"),
          visible: this.selectedSuppliers.length > 0,
          clearable: true,
        };
      }

      // Search filter
      config.search = {
        type: "search",
        label: this.$t("search"),
        icon: "mdi-magnify",
        value: this.filters.search,
        chipText: this.filters.search,
        visible: !!this.filters.search,
        clearable: true,
      };

      return config;
    },
    warehouseChipText() {
      if (this.selectedWarehouses.length === 1) {
        return this.selectedWarehouses[0].name;
      }
      return `${this.selectedWarehouses.length} ${this.$t("stock")}`;
    },
    formatDateRange() {
      const isSingleDate = !this.activeTab?.filters?.date_to;

      if (isSingleDate) {
        if (!this.filters.dates.from) return "";
        const d = new Date(this.filters.dates.from);
        return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1)
          .toString()
          .padStart(2, "0")}.`;
      }

      if (!this.filters.dates.from) return "";
      const formatDate = (date) => {
        const d = new Date(date);
        return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1)
          .toString()
          .padStart(2, "0")}.`;
      };
      if (this.filters.dates.from === this.filters.dates.to) {
        return formatDate(this.filters.dates.from);
      }
      return `${formatDate(this.filters.dates.from)} - ${formatDate(
        this.filters.dates.to
      )}`;
    },
  },
  methods: {
    handleFilterUpdate({ key, value }) {
      if (key === "dates") {
        // Check if single date or date range based on active tab
        if (this.activeTab?.filters?.date_from && !this.activeTab?.filters?.date_to) {
          this.$store.commit("warehouse/SET_DATE", [value, value]);
        } else {
          this.$store.commit("warehouse/SET_DATE", value);
        }
      } else if (key === "warehouse") {
        this.$emit("update:selectedWarehouses", value);
      } else if (key === "supplier") {
        this.$emit("update:selectedSuppliers", value);
      } else {
        this.updateFilter(key, value);
      }
    },
  },
};
</script>
